// TelcoTrack — weld billing SSOT: count stored physical `weld` rows, resolve montáž ceník.

import {
  WORK_REPORT_PRICE_LIST,
  type WorkReportPriceListItem,
} from '../work-report-price-list.constants';
import { rozvadecFiberHopKey } from './rozvadec-weld-count';
import {
  occupancyEndpointId,
  parseOccupancyEndpointId,
  type OccupancyEndpoint,
  type OccupancyWeldConnection,
} from './rozvadec-occupancy-connection';
import {
  isRozvadecBuildingDirectLink,
  isRozvadecCassettePairLink,
} from './rozvadec-weld-layout.pairs';
import {
  isCompleteWeldIncomingRef,
  isCompleteWeldTargetRef,
  rozvadecWeldIncomingRefKey,
} from './rozvadec-weld-layout.ids';
import type { RozvadecWeldLayout, RozvadecWeldLink } from './rozvadec-weld-layout.types';
import type {
  RsuOccupancy,
  RsuPatchPortRef,
  RsuTermination,
  RsuWeldConnection,
} from './rozvadec-rsu-occupancy.types';
import {
  getRozvadecCabinetType,
  resolveRozvadecCabinetTypeByAlias,
} from './rozvadec-cabinet.constants';

export const INSTALLATION_PRICE_RULE_VERSION = 1;
export const INSTALLATION_PRICE_RULE_MISSING_CS = 'nenalezeno cenové pravidlo';
export const ROZVADEC_PROVARENE_REQUIRES_WELD_CS =
  'Provařené vyžaduje alespoň jeden uložený svár.';
export const INSTALLATION_BILLING_METRIC = 'physical-weld-operation' as const;

export const PON_INSTALLATION_SAP = '324363';
export const MUK24_INSTALLATION_SAP = '326274';
export const RAY_TR3_INSTALLATION_SAP = '325715';

/** Identity SAPs with no confirmed montáž band in the current ceník. */
export const UNPRICED_INSTALLATION_SAPS = new Set(['324362', '326273', '325701']);

/** OS identity SAPs from the výkaz montáže table (same list as the ceník rows). */
export const OS_MATERIAL_SAP_LIST = [
  '325739',
  '327337',
  '325737',
  '319647',
  '320184',
  '324995',
  '325532',
  '306732',
  '324994',
  '320183',
  '318524',
  '325765',
  '325767',
  '325768',
  '325764',
  '325766',
  '325736',
  '325760',
  '325735',
  '325738',
] as const;

export const OS_MATERIAL_SAPS = new Set<string>(OS_MATERIAL_SAP_LIST);

export const INSTALLATION_PRICE_ITEM_CODES = {
  ponUpTo16: 'montaz-sloupek-pon-do-16-svaru',
  ponOver16: 'montaz-sloupek-pon-nad-16-svaru',
  osUpTo16: 'montaz-os-do-16-svaru',
  osOver16: 'montaz-os-nad-16-svaru',
  muk24: 'montaz-optika-sloupek-muk-24',
  rayTr3: 'montaz-optika-1xodf-144vl-na-rsu',
} as const;

export const ROZVADEC_CABINET_INSTALLATION_CODES = new Set<string>(
  Object.values(INSTALLATION_PRICE_ITEM_CODES),
);

export type WeldConnectionType = 'weld' | 'patch' | 'adapter-through' | 'splitter-internal';
export type WeldConnectionState = 'completed' | 'in-progress' | 'incomplete';
export type WeldBillingMetric = 'physical-weld-operation' | 'spliced-fiber';
export type WeldBillingSource = 'physical-weld' | 'legacy-manual';

export type WeldEndpoint = { id: string } | null;

export type WeldConnection = {
  id: string;
  type: WeldConnectionType;
  state: WeldConnectionState;
  from: WeldEndpoint;
  to: WeldEndpoint;
  physicalWeldOperationCount?: number;
  splicedFiberCount?: number;
  fiberRange?: { from: number; to: number };
};

export type WeldLayout = {
  connections: WeldConnection[];
};

export type WeldBillingMeta = {
  weldCountTotal: number;
  source: WeldBillingSource;
  billingMetric: WeldBillingMetric;
  priceRuleVersion: number;
  priceRuleId?: string | null;
};

export function isRibbonWeldMetrics(
  physicalWeldOperationCount?: number,
  splicedFiberCount?: number,
): boolean {
  return (physicalWeldOperationCount ?? 1) === 1 && (splicedFiberCount ?? 1) > 1;
}

export function isRozvadecCabinetInstallationItem(
  item: Pick<WorkReportPriceListItem, 'code'>,
): boolean {
  return ROZVADEC_CABINET_INSTALLATION_CODES.has((item.code ?? '').trim());
}

function shiftOccupancyEndpoint(endpoint: OccupancyEndpoint, offset: number): OccupancyEndpoint {
  if (offset === 0) return endpoint;
  switch (endpoint.kind) {
    case 'cable-fiber':
      return { ...endpoint, physicalFiber: endpoint.physicalFiber + offset };
    case 'cassette-connector':
      return { ...endpoint, slotInCassette: endpoint.slotInCassette + offset };
    case 'splitter-out':
      return { ...endpoint, output: endpoint.output + offset };
    default:
      return endpoint;
  }
}

/** Expand a range assign into one physical occupancy row per fiber. Ribbon stays one row. */
export function expandPersistedOccupancyWelds(
  welds: readonly OccupancyWeldConnection[],
): OccupancyWeldConnection[] {
  const out: OccupancyWeldConnection[] = [];
  for (const weld of welds) {
    const range = weld.fiberRange;
    if (
      !range
      || range.to < range.from
      || isRibbonWeldMetrics(weld.physicalWeldOperationCount, weld.splicedFiberCount)
    ) {
      out.push({ ...weld });
      continue;
    }
    const fromEp = parseOccupancyEndpointId(weld.fromEndpointId);
    const toEp = parseOccupancyEndpointId(weld.toEndpointId);
    if (!fromEp || !toEp) {
      out.push({ ...weld });
      continue;
    }
    const span = range.to - range.from + 1;
    for (let i = 0; i < span; i += 1) {
      out.push({
        id: span === 1 ? weld.id : `${weld.id}:${range.from + i}`,
        fromEndpointId: occupancyEndpointId(shiftOccupancyEndpoint(fromEp, i)),
        toEndpointId: occupancyEndpointId(shiftOccupancyEndpoint(toEp, i)),
        physicalWeldOperationCount: 1,
        splicedFiberCount: 1,
      });
    }
  }
  return out;
}

/** Expand an RSU range row when sequential OFSM seats exist; ribbon stays one row. */
export function expandPersistedRsuWelds(
  welds: readonly RsuWeldConnection[],
  terminations: readonly RsuTermination[] = [],
): RsuWeldConnection[] {
  const out: RsuWeldConnection[] = [];
  for (const weld of welds) {
    const range = weld.fiberRange;
    if (
      !range
      || range.to < range.from
      || isRibbonWeldMetrics(weld.physicalWeldOperationCount, weld.splicedFiberCount)
    ) {
      out.push({ ...weld });
      continue;
    }
    const startTerm = terminations.find((term) => term.id === weld.terminationId);
    const span = range.to - range.from + 1;
    for (let i = 0; i < span; i += 1) {
      const fiber = weld.physicalFiberNumber + i;
      const term = startTerm
        ? terminations.find(
            (item) =>
              item.moduleId === startTerm.moduleId
              && item.connectorPosition === startTerm.connectorPosition + i,
          )
        : null;
      out.push({
        id: span === 1 ? weld.id : `${weld.id}:${fiber}`,
        moduleId: weld.moduleId,
        terminationId: term?.id ?? weld.terminationId,
        cableId: weld.cableId,
        physicalFiberNumber: fiber,
        physicalWeldOperationCount: 1,
        splicedFiberCount: 1,
      });
    }
  }
  return out;
}

export function expandRangeConnections(connections: readonly WeldConnection[]): WeldConnection[] {
  const out: WeldConnection[] = [];
  for (const connection of connections) {
    const range = connection.fiberRange;
    if (
      !range
      || range.to < range.from
      || isRibbonWeldMetrics(connection.physicalWeldOperationCount, connection.splicedFiberCount)
    ) {
      out.push(connection);
      continue;
    }
    const span = range.to - range.from + 1;
    for (let i = 0; i < span; i += 1) {
      const n = range.from + i;
      out.push({
        ...connection,
        id: `${connection.id}:${n}`,
        fiberRange: undefined,
        from: connection.from ? { id: `${connection.from.id}:${n}` } : null,
        to: connection.to ? { id: `${connection.to.id}:${n}` } : null,
        physicalWeldOperationCount: 1,
        splicedFiberCount: 1,
      });
    }
  }
  return out;
}

export function getBillableWeldUnits(connection: WeldConnection): number {
  if (connection.type !== 'weld') return 0;
  if (connection.state !== 'completed') return 0;
  if (connection.from == null || connection.to == null) return 0;
  const ops = connection.physicalWeldOperationCount;
  if (typeof ops === 'number' && Number.isInteger(ops) && ops >= 0) return ops;
  return 1;
}

export function calculateWeldCount(layout: WeldLayout): number {
  return expandRangeConnections(layout.connections)
    .filter(
      (connection) =>
        connection.type === 'weld'
        && connection.state === 'completed'
        && connection.from != null
        && connection.to != null,
    )
    .reduce((sum, connection) => sum + getBillableWeldUnits(connection), 0);
}

function fiberPairKey(left: string, right: string): string {
  return left < right ? `${left}|${right}` : `${right}|${left}`;
}

function occupancyFiberPairKey(weld: OccupancyWeldConnection): string | null {
  const from = parseOccupancyEndpointId(weld.fromEndpointId);
  const to = parseOccupancyEndpointId(weld.toEndpointId);
  if (from?.kind !== 'cable-fiber' || to?.kind !== 'cable-fiber') return null;
  return fiberPairKey(
    rozvadecFiberHopKey(from.cableId, from.physicalFiber),
    rozvadecFiberHopKey(to.cableId, to.physicalFiber),
  );
}

function linkFiberPairKey(link: RozvadecWeldLink): string | null {
  if (
    isRozvadecCassettePairLink(link)
    && isCompleteWeldIncomingRef(link.incoming)
    && isCompleteWeldIncomingRef(link.peerIncoming)
  ) {
    return fiberPairKey(
      rozvadecWeldIncomingRefKey(link.incoming),
      rozvadecWeldIncomingRefKey(link.peerIncoming),
    );
  }
  if (isRozvadecBuildingDirectLink(link) && isCompleteWeldIncomingRef(link.incoming) && isCompleteWeldTargetRef(link.target)) {
    return fiberPairKey(
      rozvadecWeldIncomingRefKey(link.incoming),
      rozvadecFiberHopKey(link.target.cableId, link.target.physicalFiber),
    );
  }
  return null;
}

function rsuPatchRefId(ref: RsuPatchPortRef): string {
  switch (ref.kind) {
    case 'ofsm-connector':
      return `rsu-conn:${ref.moduleId}:${ref.connectorPosition}`;
    case 'splitter-in':
      return `rsu-spl:${ref.moduleId}:${ref.splitterIndex}:in`;
    case 'splitter-out':
      return `rsu-spl:${ref.moduleId}:${ref.splitterIndex}:out:${ref.output}`;
    case 'cable-fiber':
      return `rsu-fiber:${ref.cableId}:${ref.physicalFiber}`;
  }
}

/**
 * Unify MUK/PON occupancy welds, OS/KM tray directs, and RSU OFSM welds
 * into the billing `WeldLayout`. Patches are included as type `patch` (0 units).
 */
export function weldBillingLayoutFromCabinet(input: {
  layout?: Pick<RozvadecWeldLayout, 'weldConnections' | 'links' | 'patchConnections'> | null;
  rsuOccupancy?: Pick<RsuOccupancy, 'weldConnections' | 'patchConnections'> | null;
}): WeldLayout {
  const connections: WeldConnection[] = [];
  const seenFiberPairs = new Set<string>();
  const remember = (key: string | null): boolean => {
    if (!key || seenFiberPairs.has(key)) return false;
    seenFiberPairs.add(key);
    return true;
  };

  const layout = input.layout;
  if (layout) {
    for (const weld of layout.weldConnections ?? []) {
      if (!weld.fromEndpointId || !weld.toEndpointId) continue;
      const pairKey = occupancyFiberPairKey(weld);
      if (pairKey && !remember(pairKey)) continue;
      connections.push({
        id: weld.id,
        type: 'weld',
        state: 'completed',
        from: { id: weld.fromEndpointId },
        to: { id: weld.toEndpointId },
        fiberRange: weld.fiberRange,
        physicalWeldOperationCount: weld.physicalWeldOperationCount,
        splicedFiberCount: weld.splicedFiberCount,
      });
    }
    for (const patch of layout.patchConnections ?? []) {
      connections.push({
        id: patch.id,
        type: 'patch',
        state: 'completed',
        from: { id: patch.fromEndpointId },
        to: { id: patch.toEndpointId },
      });
    }
    for (const link of layout.links ?? []) {
      if (!isRozvadecCassettePairLink(link) && !isRozvadecBuildingDirectLink(link)) continue;
      const pairKey = linkFiberPairKey(link);
      if (!pairKey || !remember(pairKey)) continue;
      if (!isCompleteWeldIncomingRef(link.incoming)) continue;
      const toId = isRozvadecCassettePairLink(link) && isCompleteWeldIncomingRef(link.peerIncoming)
        ? rozvadecWeldIncomingRefKey(link.peerIncoming)
        : isCompleteWeldTargetRef(link.target)
          ? rozvadecFiberHopKey(link.target.cableId, link.target.physicalFiber)
          : '';
      if (!toId) continue;
      connections.push({
        id: link.id,
        type: 'weld',
        state: 'completed',
        from: { id: rozvadecWeldIncomingRefKey(link.incoming) },
        to: { id: toId },
      });
    }
  }

  const rsu = input.rsuOccupancy;
  if (rsu) {
    for (const weld of rsu.weldConnections ?? []) {
      if (!weld.cableId.trim() || !weld.terminationId.trim()) continue;
      if (!Number.isInteger(weld.physicalFiberNumber) || weld.physicalFiberNumber < 1) continue;
      connections.push({
        id: weld.id,
        type: 'weld',
        state: 'completed',
        from: { id: `rsu-fiber:${weld.cableId}:${weld.physicalFiberNumber}` },
        to: { id: `rsu-term:${weld.terminationId}` },
        fiberRange: weld.fiberRange,
        physicalWeldOperationCount: weld.physicalWeldOperationCount,
        splicedFiberCount: weld.splicedFiberCount,
      });
    }
    for (const patch of rsu.patchConnections ?? []) {
      connections.push({
        id: patch.id,
        type: 'patch',
        state: 'completed',
        from: { id: rsuPatchRefId(patch.source) },
        to: { id: rsuPatchRefId(patch.target) },
      });
    }
  }

  return { connections };
}

export function calculateCabinetWeldCount(input: {
  layout?: Pick<RozvadecWeldLayout, 'weldConnections' | 'links' | 'patchConnections'> | null;
  rsuOccupancy?: Pick<RsuOccupancy, 'weldConnections' | 'patchConnections'> | null;
}): number {
  return calculateWeldCount(weldBillingLayoutFromCabinet(input));
}

function priceItemByCode(code: string): WorkReportPriceListItem | null {
  return WORK_REPORT_PRICE_LIST.find((item) => item.code === code) ?? null;
}

export function resolveInstallationPrice(
  materialSap: string,
  weldCount: number,
): WorkReportPriceListItem | null {
  const sap = materialSap.trim();
  if (sap === PON_INSTALLATION_SAP) {
    if (weldCount === 0) return null;
    return weldCount <= 16
      ? priceItemByCode(INSTALLATION_PRICE_ITEM_CODES.ponUpTo16)
      : priceItemByCode(INSTALLATION_PRICE_ITEM_CODES.ponOver16);
  }
  if (OS_MATERIAL_SAPS.has(sap)) {
    if (weldCount === 0) return null;
    return weldCount <= 16
      ? priceItemByCode(INSTALLATION_PRICE_ITEM_CODES.osUpTo16)
      : priceItemByCode(INSTALLATION_PRICE_ITEM_CODES.osOver16);
  }
  if (sap === MUK24_INSTALLATION_SAP) return priceItemByCode(INSTALLATION_PRICE_ITEM_CODES.muk24);
  if (sap === RAY_TR3_INSTALLATION_SAP) return priceItemByCode(INSTALLATION_PRICE_ITEM_CODES.rayTr3);
  return null;
}

export function resolveInstallationIdentitySap(
  derivedFromSap: string | null | undefined,
  materials: readonly { sapCode?: string | null }[] | null | undefined,
  typeId?: string | null,
): string | null {
  const derived = (derivedFromSap ?? '').trim();
  const type = getRozvadecCabinetType(typeId) ?? resolveRozvadecCabinetTypeByAlias(typeId);
  // RSU/ODF family: priced RAY-TR3 sestava (325715), not the unpriced cabinet body (325701).
  if (type?.id === 'RSU_ODF') return RAY_TR3_INSTALLATION_SAP;
  if (derived) return derived;
  const saps = (materials ?? []).map((item) => (item.sapCode ?? '').trim()).filter(Boolean);
  for (const sap of type?.identitySapCodes ?? []) {
    const code = sap.trim();
    if (code && !saps.includes(code)) saps.push(code);
  }
  const priced = [
    PON_INSTALLATION_SAP,
    ...OS_MATERIAL_SAP_LIST,
    MUK24_INSTALLATION_SAP,
    RAY_TR3_INSTALLATION_SAP,
  ];
  for (const sap of priced) {
    if (saps.includes(sap)) return sap;
  }
  for (const sap of UNPRICED_INSTALLATION_SAPS) {
    if (saps.includes(sap)) return sap;
  }
  return null;
}

export function resolveCabinetInstallationPrice(input: {
  derivedFromSap?: string | null;
  typeId?: string | null;
  materialSaps?: readonly string[] | null;
  materials?: readonly { sapCode?: string | null }[] | null;
  weldCount: number;
}): WorkReportPriceListItem | null {
  const materials = input.materials
    ?? (input.materialSaps ?? []).map((sapCode) => ({ sapCode }));
  const sap = resolveInstallationIdentitySap(input.derivedFromSap, materials, input.typeId);
  return sap ? resolveInstallationPrice(sap, input.weldCount) : null;
}

export function formatInstallationPricePreview(item: WorkReportPriceListItem | null): string {
  if (!item) return INSTALLATION_PRICE_RULE_MISSING_CS;
  return `${item.pricePerUnit} Kč`;
}

export function buildWeldBillingMeta(input: {
  weldCountTotal: number;
  source: WeldBillingSource;
  priceRuleId?: string | null;
}): WeldBillingMeta {
  return {
    weldCountTotal: input.weldCountTotal,
    source: input.source,
    billingMetric: INSTALLATION_BILLING_METRIC,
    priceRuleVersion: INSTALLATION_PRICE_RULE_VERSION,
    priceRuleId: input.priceRuleId ?? null,
  };
}

export function readWeldBillingMeta(metadata: unknown): WeldBillingMeta | null {
  if (metadata == null || typeof metadata !== 'object' || Array.isArray(metadata)) return null;
  const raw = (metadata as Record<string, unknown>)['weldBilling'];
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const rec = raw as Record<string, unknown>;
  const total = rec['weldCountTotal'];
  if (typeof total !== 'number' || !Number.isFinite(total) || total < 0) return null;
  const source = rec['source'] === 'legacy-manual' ? 'legacy-manual' : 'physical-weld';
  const metric = rec['billingMetric'] === 'spliced-fiber' ? 'spliced-fiber' : 'physical-weld-operation';
  const version = rec['priceRuleVersion'];
  return {
    weldCountTotal: Math.floor(total),
    source,
    billingMetric: metric,
    priceRuleVersion:
      typeof version === 'number' && Number.isInteger(version) && version > 0
        ? version
        : INSTALLATION_PRICE_RULE_VERSION,
    priceRuleId: typeof rec['priceRuleId'] === 'string' ? rec['priceRuleId'] : null,
  };
}
